/**
 * DAILY CORE ACTION SERVICE
 * 
 * Manages micro-victory system (30-90 seconds daily action)
 * Exactly 1 minimal action per day
 * Always achievable
 * Drives retention and streak logic
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import { awardXP, XP_ACTIONS } from './gamificationService';
import { updateRitualProgress } from './ritualService';
import { addDailyStamp } from './calendarService';

export type CoreActionType = 'workout_confirm' | 'meal_mark' | 'reflection' | 'custom';

export interface DailyCoreAction {
  type: CoreActionType;
  completed: boolean;
  completedAt?: string; // ISO date string
  text?: string; // For reflection type
}

/**
 * Complete today's core action
 * This is the MINIMUM requirement to maintain streak
 */
export const completeDailyCoreAction = async (
  userId: string,
  type: CoreActionType,
  text?: string
): Promise<{ success: boolean; streakMaintained: boolean }> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    return { success: false, streakMaintained: false };
  }
  
  const user = userSnap.data() as User;
  const today = new Date().toISOString().split('T')[0];
  
  // Check if already completed today
  if (user.dailyCoreActionCompleted && user.dailyCoreActionDate === today) {
    return { success: true, streakMaintained: true };
  }
  
  // Update core action
  const wasStreakMaintained = user.dailyCoreActionDate === today || 
    (user.dailyCoreActionDate && 
     new Date(user.dailyCoreActionDate).getTime() === new Date(today).getTime() - 86400000);
  
  const newConsecutive = wasStreakMaintained 
    ? (user.consecutiveCoreActions || 0) + 1 
    : 1;
  
  await updateDoc(userRef, {
    dailyCoreActionCompleted: true,
    dailyCoreActionDate: today,
    dailyCoreActionType: type,
    dailyCoreActionText: text || user.dailyCoreActionText,
    consecutiveCoreActions: newConsecutive
  });
  
  // Award XP for completing core action
  await awardXP(userId, XP_ACTIONS.DAILY_STAMP, 'daily_core_action');
  
  // Add calendar stamp (replaces streak)
  await addDailyStamp(userId, 'standard');
  
  // Update ritual progress if ritual is active
  if (!user.ritualCompleted) {
    await updateRitualProgress(userId);
  }
  
  console.log(`[CORE_ACTION] Completed daily core action for user ${userId}: ${type}`);
  
  return { 
    success: true, 
    streakMaintained: wasStreakMaintained || newConsecutive > 1 
  };
};

/**
 * Check if today's core action is completed
 */
export const isTodayCoreActionCompleted = (user: User): boolean => {
  const today = new Date().toISOString().split('T')[0];
  return user.dailyCoreActionCompleted === true && user.dailyCoreActionDate === today;
};

/**
 * Get today's core action status
 */
export const getTodayCoreAction = (user: User): DailyCoreAction | null => {
  const today = new Date().toISOString().split('T')[0];
  
  if (user.dailyCoreActionDate === today) {
    return {
      type: user.dailyCoreActionType || 'workout_confirm',
      completed: user.dailyCoreActionCompleted || false,
      completedAt: user.dailyCoreActionDate,
      text: user.dailyCoreActionText
    };
  }
  
  return null;
};

/**
 * Check if user missed core action (breaks streak)
 * Called at end of day or next day check
 */
export const checkMissedCoreAction = async (userId: string): Promise<boolean> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) return false;
  
  const user = userSnap.data() as User;
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  
  // If yesterday's action wasn't completed, streak is broken
  if (user.dailyCoreActionDate !== yesterday && user.dailyCoreActionDate !== today) {
    // Reset consecutive count
    await updateDoc(userRef, {
      consecutiveCoreActions: 0,
      dailyCoreActionCompleted: false
    });
    
    console.log(`[CORE_ACTION] Missed core action for user ${userId}, streak reset`);
    return true;
  }
  
  return false;
};

